import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import StudioBusinessValueBand from "../components/studio/StudioBusinessValueBand";
import StudioDemoLauncher from "../components/studio/StudioDemoLauncher";
import StudioFlowGuide from "../components/studio/StudioFlowGuide";
import StudioKnowledgeAssistant from "../components/studio/StudioKnowledgeAssistant";
import StudioPremiumStepContent from "../components/studio/StudioPremiumStepContent";
import StudioSidebarCard from "../components/studio/StudioSidebarCard";
import StudioStepRail from "../components/studio/StudioStepRail";
import StudioWorkspaceStatusBar from "../components/studio/StudioWorkspaceStatusBar";
import type { StudioDeploymentDecision } from "../lib/studio/studioDeploymentState";
import { getStudioImproveLoopReadModel } from "../lib/studio/studioImproveReadModel";
import {
  STUDIO_DEMO_LAUNCHER_PRESETS,
  getStudioDemoLauncherPreset,
  isStudioDemoLauncherId,
  type StudioDemoLauncherId,
} from "../lib/studio/studioDemoLauncher";
import { buildStudioPremiumDemoModel } from "../lib/studio/studioPremiumDemo";
import { loadStudioPreviewEvidenceLedger } from "../lib/studio/studioPreviewEvidenceLedger";
import {
  loadStudioWorkspaceStorage,
  resetStudioWorkspaceStorage,
  saveStudioWorkspaceStorage,
} from "../lib/studio/studioWorkspaceStorage";
import {
  createDefaultStudioWorkspaceState,
  type StudioWorkspaceState,
} from "../lib/studio/studioWorkspaceState";
import {
  getVetCanStudioReadModel,
  type VetCanStudioChecklistState,
  type VetCanStudioReadModel,
  type VetCanStudioStepId,
  type VetCanStudioStepState,
} from "../lib/studio/studioReadModel";
import StudioLaunchCandidateCloseout from "../components/studio/StudioLaunchCandidateCloseout";

function stateStyles(state: VetCanStudioStepState) {
  if (state === "blocked") {
    return {
      background: "rgba(162, 62, 62, 0.12)",
      color: "#812d2d",
      dot: "#a23e3e",
    };
  }
  if (state === "draft") {
    return {
      background: "rgba(181, 141, 34, 0.16)",
      color: "#6d5210",
      dot: "#8f6e16",
    };
  }
  return {
    background: "rgba(44, 93, 67, 0.14)",
    color: "#214735",
    dot: "#2c5d43",
  };
}

function checklistStyles(state: VetCanStudioChecklistState) {
  if (state === "blocked") {
    return {
      background: "rgba(162, 62, 62, 0.12)",
      color: "#7c2f2f",
    };
  }
  if (state === "review") {
    return {
      background: "rgba(181, 141, 34, 0.16)",
      color: "#6d5210",
    };
  }
  return {
    background: "rgba(44, 93, 67, 0.12)",
    color: "#224634",
  };
}

function controlCardStyles(state: VetCanStudioStepState) {
  if (state === "blocked") {
    return {
      border: "1px solid rgba(162, 62, 62, 0.22)",
      background:
        "linear-gradient(180deg, rgba(255, 243, 240, 0.96), rgba(255, 249, 246, 0.98))",
    };
  }
  return {
    border: "1px solid rgba(41, 84, 103, 0.16)",
    background:
      "linear-gradient(180deg, rgba(245, 249, 250, 0.96), rgba(255, 252, 248, 0.98))",
  };
}

function storyCard(label: string, value: string, detail: string) {
  return (
    <div
      style={{
        borderRadius: 22,
        padding: "0.95rem 1rem",
        background: "rgba(247, 244, 238, 0.11)",
      }}
    >
      <div
        style={{
          fontSize: "0.76rem",
          textTransform: "uppercase",
          letterSpacing: "0.12em",
          opacity: 0.72,
        }}
      >
        {label}
      </div>
      <div style={{ marginTop: "0.35rem", fontSize: "1rem", fontWeight: 700 }}>
        {value}
      </div>
      <p
        style={{
          margin: "0.35rem 0 0",
          fontSize: "0.86rem",
          lineHeight: 1.55,
          color: "rgba(247, 244, 238, 0.84)",
        }}
      >
        {detail}
      </p>
    </div>
  );
}

const STUDIO_START_HERE_STEPS = [
  {
    label: "Studio",
    title: "Shape the working draft",
    detail:
      "Use Studio to define the assistant, choose from the approved voice catalog, review the draft, and prepare internal signoff and handoff notes.",
  },
  {
    label: "Preview",
    title: "Rehearse before install",
    detail:
      "Use Preview to test the current draft, inspect evidence, and rehearse safely without touching live clinic traffic.",
  },
  {
    label: "Launch control",
    title: "Read the safety check",
    detail:
      "Launch control explains what is ready for review and what still blocks live use. It is not a deploy button.",
  },
  {
    label: "Dashboard",
    title: "Confirm live read-only truth",
    detail:
      "Use the Dashboard last to confirm what is actually happening now in the live system.",
  },
] as const;

function readInitialStudioStep(
  stepOrder: readonly VetCanStudioStepId[],
  storedStepId: VetCanStudioStepId
) {
  if (typeof window === "undefined") {
    return storedStepId;
  }

  const requestedStep = new URLSearchParams(window.location.search).get("step");

  if (stepOrder.includes(requestedStep as VetCanStudioStepId)) {
    return requestedStep as VetCanStudioStepId;
  }

  return stepOrder.includes(storedStepId) ? storedStepId : stepOrder[0];
}

function readInitialStudioDemoId() {
  if (typeof window === "undefined") {
    return STUDIO_DEMO_LAUNCHER_PRESETS[0].id;
  }

  const requestedDemo = new URLSearchParams(window.location.search).get("demo");
  return isStudioDemoLauncherId(requestedDemo)
    ? requestedDemo
    : STUDIO_DEMO_LAUNCHER_PRESETS[0].id;
}

function syncStudioStepToWindow(
  stepId: VetCanStudioStepId,
  demoId: StudioDemoLauncherId
) {
  if (typeof window === "undefined") {
    return;
  }

  const url = new URL(window.location.href);
  url.searchParams.set("step", stepId);
  url.searchParams.set("demo", demoId);
  window.history.replaceState(
    window.history.state,
    "",
    `${url.pathname}${url.search}${url.hash}`
  );
}

function hasApprovalSensitiveWorkspaceChange(
  current: StudioWorkspaceState,
  next: StudioWorkspaceState
) {
  return (
    current.assistantBrief.operatorDescription !==
      next.assistantBrief.operatorDescription ||
    current.voicePersona.selectedPersonaId !== next.voicePersona.selectedPersonaId ||
    current.voicePersona.selectedVoiceOptionId !==
      next.voicePersona.selectedVoiceOptionId ||
    current.preview.selectedChannel !== next.preview.selectedChannel ||
    current.humanization.warmth !== next.humanization.warmth ||
    current.humanization.concision !== next.humanization.concision ||
    current.humanization.professionalism !== next.humanization.professionalism ||
    current.humanization.closeQuality !== next.humanization.closeQuality ||
    current.deployment.notes !== next.deployment.notes ||
    current.deployment.readinessSelections.personaReviewed !==
      next.deployment.readinessSelections.personaReviewed ||
    current.deployment.readinessSelections.routingTruthConfirmed !==
      next.deployment.readinessSelections.routingTruthConfirmed ||
    current.deployment.readinessSelections.previewLaneConfirmed !==
      next.deployment.readinessSelections.previewLaneConfirmed ||
    current.deployment.approvalSelections.currentBlockersReviewed !==
      next.deployment.approvalSelections.currentBlockersReviewed ||
    current.deployment.approvalSelections.canaryApprovalExternalConfirmed !==
      next.deployment.approvalSelections.canaryApprovalExternalConfirmed
  );
}

export default function VetCanStudio(props: { model?: VetCanStudioReadModel }) {
  const model = props.model || getVetCanStudioReadModel();
  const improveLoopModel = useMemo(
    () =>
      getStudioImproveLoopReadModel({
        evidenceEntries: loadStudioPreviewEvidenceLedger().entries,
      }),
    []
  );
  const [workspaceLoad] = useState(() => loadStudioWorkspaceStorage());
  const [initialStep] = useState<VetCanStudioStepId>(() =>
    readInitialStudioStep(
      model.stepOrder,
      workspaceLoad.state.session.lastActiveStepId
    )
  );
  const [workspaceState, setWorkspaceState] = useState(() => ({
    ...workspaceLoad.state,
    session: {
      ...workspaceLoad.state.session,
      lastActiveStepId: initialStep,
    },
  }));
  const [activeStepId, setActiveStepId] = useState<VetCanStudioStepId>(initialStep);
  const [activeDemoId, setActiveDemoId] = useState<StudioDemoLauncherId>(
    () => readInitialStudioDemoId()
  );
  const [startHereOpen, setStartHereOpen] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(workspaceLoad.savedAt);
  const [draftRestored, setDraftRestored] = useState(workspaceLoad.restored);
  const [deploymentDecision, setDeploymentDecision] =
    useState<StudioDeploymentDecision | null>(null);
  const skipFirstAutosaveRef = useRef(true);
  const suppressNextAutosaveRef = useRef(false);

  const premiumDemo = useMemo(
    () =>
      buildStudioPremiumDemoModel({
        studioModel: model,
        workspaceState,
        improveLoopModel,
      }),
    [improveLoopModel, model, workspaceState]
  );
  const activeDemoPreset = useMemo(
    () => getStudioDemoLauncherPreset(activeDemoId),
    [activeDemoId]
  );

  useEffect(() => {
    syncStudioStepToWindow(activeStepId, activeDemoId);
  }, [activeDemoId, activeStepId]);

  useEffect(() => {
    if (skipFirstAutosaveRef.current) {
      skipFirstAutosaveRef.current = false;
      return;
    }

    if (suppressNextAutosaveRef.current) {
      suppressNextAutosaveRef.current = false;
      return;
    }

    const result = saveStudioWorkspaceStorage(workspaceState);
    if (result.savedAt) {
      setLastSavedAt(result.savedAt);
    }
  }, [workspaceState]);

  function patchWorkspace(
    updater: (current: typeof workspaceState) => typeof workspaceState,
    options?: {
      preserveRecordedApproval?: boolean;
    }
  ) {
    setWorkspaceState((current) => {
      const next = updater(current);

      if (options?.preserveRecordedApproval) {
        return next;
      }

      if (
        current.deployment.approvalRecordedAt &&
        hasApprovalSensitiveWorkspaceChange(current, next)
      ) {
        return {
          ...next,
          deployment: {
            ...next.deployment,
            approvalRecordedAt: null,
          },
        };
      }

      return next;
    });
  }

  function selectStep(stepId: VetCanStudioStepId, demoId?: StudioDemoLauncherId) {
    if (demoId) {
      setActiveDemoId(demoId);
    }
    setActiveStepId(stepId);
    patchWorkspace((current) => ({
      ...current,
      session: {
        ...current.session,
        lastActiveStepId: stepId,
      },
    }));
  }

  function handleSaveDraft() {
    const result = saveStudioWorkspaceStorage(workspaceState);
    if (result.savedAt) {
      setLastSavedAt(result.savedAt);
    }
  }

  function handleResetDraft() {
    const resetState = createDefaultStudioWorkspaceState();
    suppressNextAutosaveRef.current = true;
    resetStudioWorkspaceStorage();
    setDraftRestored(false);
    setLastSavedAt(null);
    setActiveStepId(model.stepOrder[0]);
    setWorkspaceState({
      ...resetState,
      session: {
        ...resetState.session,
        lastActiveStepId: model.stepOrder[0],
      },
    });
  }

  const activeStepIndex = Math.max(model.stepOrder.indexOf(activeStepId), 0);
  const activeStep = model.steps.find((step) => step.id === activeStepId) || model.steps[0];
  const previousStepId =
    activeStepIndex > 0 ? model.stepOrder[activeStepIndex - 1] : null;
  const nextStepId =
    activeStepIndex < model.stepOrder.length - 1
      ? model.stepOrder[activeStepIndex + 1]
      : null;
  const previousStep = previousStepId
    ? model.steps.find((step) => step.id === previousStepId) || null
    : null;
  const nextStep = nextStepId
    ? model.steps.find((step) => step.id === nextStepId) || null
    : null;
  const activeState = stateStyles(activeStep.state);

  return (
    <main
      style={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at top left, rgba(191, 222, 231, 0.45), transparent 34%), linear-gradient(180deg, #f7f1e8 0%, #fcfbf8 34%, #f2ece4 100%)",
        color: "#231f1a",
      }}
    >
      <div
        style={{
          maxWidth: 1440,
          margin: "0 auto",
          padding: "2rem 1.25rem 3rem",
        }}
      >
        <section
          style={{
            borderRadius: 34,
            padding: "1.5rem",
            background:
              "linear-gradient(135deg, rgba(20, 35, 42, 0.96), rgba(41, 84, 103, 0.92) 58%, rgba(209, 177, 129, 0.9) 124%)",
            color: "#f7f4ee",
            boxShadow: "0 34px 60px rgba(28, 39, 45, 0.22)",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1.8fr) minmax(280px, 1fr)",
              gap: "1rem",
            }}
          >
            <div>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.55rem",
                  padding: "0.38rem 0.8rem",
                  borderRadius: 999,
                  background: "rgba(247, 244, 238, 0.12)",
                  fontSize: "0.8rem",
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  fontWeight: 700,
                }}
              >
                Install-week walkthrough
              </div>
              <h1
                style={{
                  margin: "1rem 0 0.75rem",
                  fontSize: "clamp(2rem, 4vw, 3.7rem)",
                  lineHeight: 1,
                  fontFamily: "Georgia, 'Times New Roman', serif",
                  fontWeight: 700,
                }}
              >
                {model.title}
              </h1>
              <p
                style={{
                  margin: 0,
                  maxWidth: 760,
                  fontSize: "1.05rem",
                  lineHeight: 1.65,
                  color: "rgba(247, 244, 238, 0.9)",
                }}
              >
                {model.subtitle}
              </p>
              <p
                style={{
                  margin: "0.8rem 0 0",
                  maxWidth: 760,
                  fontSize: "0.96rem",
                  lineHeight: 1.65,
                  color: "rgba(247, 244, 238, 0.86)",
                }}
              >
                Studio is where the working draft lives. Preview is where the team
                rehearses it. The Dashboard is the live read-only view. Launch
                control is a safety check that explains what still needs review
                before anyone treats the system as live.
              </p>
              <div
                style={{
                  marginTop: "0.95rem",
                  display: "flex",
                  gap: "0.75rem",
                  flexWrap: "wrap",
                }}
              >
                <Link
                  to={`/studio/preview?returnStep=${activeStep.id}&demo=${activeDemoId}`}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 999,
                    padding: "0.76rem 1rem",
                    textDecoration: "none",
                    color: "#f7f4ee",
                    border: "1px solid rgba(247, 244, 238, 0.2)",
                    background: "rgba(247, 244, 238, 0.08)",
                    fontWeight: 700,
                  }}
                >
                  Open Preview & Test Center
                </Link>
                <Link
                  to="/metrics"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 999,
                    padding: "0.76rem 1rem",
                    textDecoration: "none",
                    color: "#173847",
                    background: "#f7f4ee",
                    fontWeight: 700,
                  }}
                >
                  Open Ops Dashboard
                </Link>
                <button
                  id="studio-start-here-toggle"
                  type="button"
                  aria-expanded={startHereOpen}
                  aria-controls="studio-start-here-panel"
                  onClick={() => setStartHereOpen((current) => !current)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 999,
                    padding: "0.76rem 1rem",
                    border: "1px solid rgba(247, 244, 238, 0.2)",
                    background: startHereOpen
                      ? "rgba(247, 244, 238, 0.18)"
                      : "rgba(10, 20, 26, 0.18)",
                    color: "#f7f4ee",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  {startHereOpen ? "Hide Start Here" : "Start Here"}
                </button>
              </div>
              {startHereOpen ? (
                <section
                  id="studio-start-here-panel"
                  aria-labelledby="studio-start-here-toggle"
                  style={{
                    marginTop: "1rem",
                    borderRadius: 24,
                    border: "1px solid rgba(247, 244, 238, 0.16)",
                    background: "rgba(10, 20, 26, 0.2)",
                    padding: "1rem",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.76rem",
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      color: "rgba(247, 244, 238, 0.72)",
                      fontWeight: 700,
                    }}
                  >
                    Start here guide
                  </div>
                  <p
                    style={{
                      margin: "0.5rem 0 0",
                      maxWidth: 760,
                      color: "rgba(247, 244, 238, 0.9)",
                      lineHeight: 1.65,
                    }}
                  >
                    Use this order on install day: start in Studio, move to
                    Preview, review launch control as the safety check, then use
                    the Dashboard last for the live read-only view.
                  </p>
                  <p
                    style={{
                      margin: "0.6rem 0 0",
                      maxWidth: 760,
                      color: "rgba(247, 244, 238, 0.84)",
                      lineHeight: 1.65,
                    }}
                  >
                    Live verification comes last: confirm the Dashboard and install
                    integrations only after Preview and launch control look safe.
                    If either one regresses, stop new edits and follow the
                    rollback plan.
                  </p>
                  <p
                    style={{
                      margin: "0.6rem 0 0",
                      maxWidth: 760,
                      color: "rgba(247, 244, 238, 0.84)",
                      lineHeight: 1.65,
                    }}
                  >
                    Voice choice stays simple here: Erin is the current approved
                    ElevenLabs default in Studio, Studio saves your selected
                    approved voice in the draft and handoff package, Preview
                    shows current runtime truth, and live inbound stays on
                    Twilio / Polly unless the separate ElevenLabs bridge is
                    intentionally enabled and ready.
                  </p>
                  <div
                    style={{
                      marginTop: "0.9rem",
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                      gap: "0.75rem",
                    }}
                  >
                    {STUDIO_START_HERE_STEPS.map((step, index) => (
                      <div
                        key={step.label}
                        style={{
                          borderRadius: 20,
                          padding: "0.9rem",
                          background: "rgba(247, 244, 238, 0.1)",
                        }}
                      >
                        <div
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 28,
                            height: 28,
                            borderRadius: 999,
                            background: "rgba(247, 244, 238, 0.16)",
                            color: "#f7f4ee",
                            fontWeight: 700,
                          }}
                        >
                          {index + 1}
                        </div>
                        <div
                          style={{
                            marginTop: "0.6rem",
                            fontSize: "0.76rem",
                            letterSpacing: "0.12em",
                            textTransform: "uppercase",
                            color: "rgba(247, 244, 238, 0.72)",
                            fontWeight: 700,
                          }}
                        >
                          {step.label}
                        </div>
                        <div style={{ marginTop: "0.35rem", fontSize: "1rem", fontWeight: 700 }}>
                          {step.title}
                        </div>
                        <p
                          style={{
                            margin: "0.35rem 0 0",
                            color: "rgba(247, 244, 238, 0.84)",
                            lineHeight: 1.55,
                            fontSize: "0.86rem",
                          }}
                        >
                          {step.detail}
                        </p>
                      </div>
                    ))}
                  </div>
                </section>
              ) : null}
              <div
                style={{
                  marginTop: "1.1rem",
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: "0.85rem",
                }}
              >
                {storyCard(
                  "Studio",
                  "Working draft",
                  `${premiumDemo.brief.assistantName} stays editable here for internal review and signoff prep.`
                )}
                {storyCard(
                  "Preview",
                  "Runtime rehearsal",
                  "Use Preview to test the current draft, voice lane, and evidence without touching live clinic traffic."
                )}
                {storyCard(
                  "Dashboard",
                  "Live read-only view",
                  "Use the Dashboard to confirm live operational truth after Studio and Preview are understood."
                )}
                {storyCard(
                  "Launch control",
                  "Safety check only",
                  "It explains blockers and next review steps. It is not a deployment button."
                )}
              </div>
              <p
                style={{
                  margin: "0.95rem 0 0",
                  color: "rgba(247, 244, 238, 0.84)",
                  lineHeight: 1.65,
                  fontSize: "0.9rem",
                }}
              >
                Need a quick client-safe answer? Use the{" "}
                <a
                  href="#studio-guided-help"
                  style={{
                    color: "#f7f4ee",
                    fontWeight: 700,
                    textDecoration: "underline",
                    textUnderlineOffset: "0.15em",
                  }}
                >
                  guided question bar
                </a>{" "}
                below the draft status bar for bounded help on Studio, Preview,
                Dashboard, approvals, and current system boundaries.
              </p>
              <p
                style={{
                  margin: "0.9rem 0 0",
                  color: "rgba(247, 244, 238, 0.82)",
                  fontSize: "0.88rem",
                }}
              >
                Current demo focus: <strong>{activeDemoPreset.label}</strong>. Workspace mode:{" "}
                <strong>{model.assistant.operatorMode}</strong>.
              </p>
            </div>

            <div
              style={{
                display: "grid",
                gap: "0.8rem",
                alignContent: "start",
              }}
            >
              {model.controls.map((control) => {
                const style = stateStyles(control.state);
                return (
                  <div
                    key={control.label}
                    style={{
                      borderRadius: 24,
                      padding: "1rem",
                      border: "1px solid rgba(247, 244, 238, 0.12)",
                      background: "rgba(10, 20, 26, 0.18)",
                      backdropFilter: "blur(12px)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: "0.75rem",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ fontSize: "0.9rem", fontWeight: 700 }}>
                        {control.label}
                      </span>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "0.4rem",
                          borderRadius: 999,
                          padding: "0.2rem 0.58rem",
                          background: style.background,
                          color: style.color,
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          textTransform: "capitalize",
                        }}
                      >
                        <span
                          aria-hidden="true"
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: 999,
                            background: style.dot,
                          }}
                        />
                        {control.state}
                      </span>
                    </div>
                    <div style={{ marginTop: "0.4rem", fontSize: "1.02rem", fontWeight: 700 }}>
                      {control.value}
                    </div>
                    <p
                      style={{
                        margin: "0.45rem 0 0",
                        fontSize: "0.9rem",
                        lineHeight: 1.5,
                        color: "rgba(247, 244, 238, 0.84)",
                      }}
                    >
                      {control.detail}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <StudioWorkspaceStatusBar
          draftRestored={draftRestored}
          lastSavedAt={lastSavedAt}
          onSave={handleSaveDraft}
          onReset={handleResetDraft}
        />

        <StudioKnowledgeAssistant />

        <div
          style={{
            marginTop: "0.9rem",
            borderRadius: 20,
            border: "1px solid rgba(41, 84, 103, 0.12)",
            background:
              "linear-gradient(180deg, rgba(240, 248, 250, 0.86), rgba(248, 251, 252, 0.94))",
            padding: "0.9rem 1rem",
            color: "#39535f",
            lineHeight: 1.65,
          }}
        >
          Studio prepares the draft, internal signoff, and handoff package. Live
          rollout still belongs to the separate operator workflow.
        </div>

        <StudioBusinessValueBand
          title={model.businessUserExperience.title}
          summary={model.businessUserExperience.summary}
          visibleNow={model.businessUserExperience.visibleNow}
          gatedNow={model.businessUserExperience.gatedNow}
          nextMove={model.businessUserExperience.nextMove}
        />

        <StudioLaunchCandidateCloseout
          title={model.launchCandidateCloseout.title}
          summary={model.launchCandidateCloseout.summary}
          scoreLabel={model.launchCandidateCloseout.scoreLabel}
          scoreValue={model.launchCandidateCloseout.scoreValue}
          scoreDetail={model.launchCandidateCloseout.scoreDetail}
          proofSummary={model.launchCandidateCloseout.proofSummary}
          knownBlockers={model.launchCandidateCloseout.knownBlockers}
          nextOperatorWorkflow={model.launchCandidateCloseout.nextOperatorWorkflow}
        />

        <StudioDemoLauncher
          activeDemoId={activeDemoId}
          onSelectDemo={setActiveDemoId}
          onOpenStudioStep={(stepId, demoId) => selectStep(stepId, demoId)}
        />

        <StudioFlowGuide
          stages={premiumDemo.flowStages}
          activeStepId={activeStep.id}
          onSelectStage={selectStep}
        />

        <section
          style={{
            marginTop: "1.25rem",
            display: "grid",
            gridTemplateColumns: "minmax(260px, 320px) minmax(0, 1fr) minmax(260px, 320px)",
            gap: "1rem",
            alignItems: "start",
          }}
        >
          <div style={{ position: "sticky", top: 18 }}>
            <StudioStepRail
              steps={model.steps}
              activeStepId={activeStep.id}
              onSelectStep={selectStep}
            />
          </div>

          <section
            style={{
              borderRadius: 32,
              border: "1px solid rgba(54, 44, 35, 0.1)",
              background: "rgba(255, 252, 248, 0.96)",
              padding: "1.4rem",
              boxShadow: "0 28px 60px rgba(77, 61, 44, 0.08)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "1rem",
                alignItems: "flex-start",
                flexWrap: "wrap",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "0.78rem",
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    color: "#7b726a",
                    fontWeight: 700,
                  }}
                >
                  {activeStep.eyebrow}
                </div>
                <h2
                  style={{
                    margin: "0.45rem 0 0",
                    fontSize: "clamp(1.7rem, 3vw, 2.4rem)",
                    lineHeight: 1.05,
                    fontFamily: "Georgia, 'Times New Roman', serif",
                  }}
                >
                  {activeStep.title}
                </h2>
              </div>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.45rem",
                  borderRadius: 999,
                  padding: "0.34rem 0.72rem",
                  background: activeState.background,
                  color: activeState.color,
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  textTransform: "capitalize",
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 999,
                    background: activeState.dot,
                  }}
                />
                {activeStep.state}
              </span>
            </div>

            <p
              style={{
                margin: "0.9rem 0 0",
                fontSize: "1rem",
                lineHeight: 1.7,
                color: "#4d453d",
              }}
            >
              {activeStep.summary}
            </p>

            <div
              style={{
                marginTop: "1.2rem",
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "0.9rem",
              }}
            >
              {activeStep.fields.map((field) => (
                <div
                  key={field.label}
                  style={{
                    borderRadius: 22,
                    border: "1px solid rgba(54, 44, 35, 0.09)",
                    background:
                      "linear-gradient(180deg, rgba(251, 248, 243, 0.98), rgba(255, 255, 255, 0.98))",
                    padding: "1rem",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.72rem",
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      color: "#85796e",
                      fontWeight: 700,
                    }}
                  >
                    {field.label}
                  </div>
                  <div
                    style={{
                      marginTop: "0.45rem",
                      color: "#26221d",
                      fontSize: "0.98rem",
                      lineHeight: 1.55,
                      fontWeight: 600,
                    }}
                  >
                    {field.value}
                  </div>
                </div>
              ))}
            </div>

            <div
              style={{
                marginTop: "1.15rem",
                display: "grid",
                gap: "0.75rem",
              }}
            >
              {activeStep.checklist.map((item) => {
                const style = checklistStyles(item.state);

                return (
                  <div
                    key={item.label}
                    style={{
                      display: "flex",
                      gap: "0.8rem",
                      alignItems: "flex-start",
                      borderRadius: 18,
                      padding: "0.85rem 0.95rem",
                      background: "#fbf7f1",
                      border: "1px solid rgba(54, 44, 35, 0.08)",
                    }}
                  >
                    <span
                      style={{
                        flexShrink: 0,
                        borderRadius: 999,
                        padding: "0.22rem 0.52rem",
                        background: style.background,
                        color: style.color,
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        textTransform: "capitalize",
                      }}
                    >
                      {item.state}
                    </span>
                    <span
                      style={{
                        color: "#403931",
                        lineHeight: 1.55,
                        fontSize: "0.95rem",
                      }}
                    >
                      {item.label}
                    </span>
                  </div>
                );
              })}
            </div>

            {activeStep.callout ? (
              <div
                style={{
                  marginTop: "1.15rem",
                  borderRadius: 24,
                  padding: "1rem 1.05rem",
                  border: "1px solid rgba(41, 84, 103, 0.14)",
                  background:
                    "linear-gradient(180deg, rgba(240, 248, 250, 0.9), rgba(248, 251, 252, 0.98))",
                }}
              >
                <div
                  style={{
                    fontSize: "0.74rem",
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: "#5b7884",
                    fontWeight: 700,
                  }}
                >
                  {activeStep.callout.title}
                </div>
                <p
                  style={{
                    margin: "0.45rem 0 0",
                    color: "#39535f",
                    lineHeight: 1.65,
                    fontSize: "0.95rem",
                  }}
                >
                  {activeStep.callout.body}
                </p>
                {activeStep.id === "preview" ? (
                  <Link
                    to="/studio/preview?returnStep=preview"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginTop: "0.85rem",
                      borderRadius: 999,
                      padding: "0.72rem 1rem",
                      textDecoration: "none",
                      background: "linear-gradient(135deg, #173847, #285466)",
                      color: "#f7f4ee",
                      fontWeight: 700,
                    }}
                  >
                    Open Preview & Test Center
                  </Link>
                ) : null}
              </div>
            ) : null}

            <StudioPremiumStepContent
              activeStepId={activeStep.id}
              operatorDescription={workspaceState.assistantBrief.operatorDescription}
              onOperatorDescriptionChange={(value) =>
                patchWorkspace((current) => ({
                  ...current,
                  assistantBrief: {
                    ...current.assistantBrief,
                    operatorDescription: value,
                  },
                }))
              }
              onVoicePersonaChange={(value) =>
                patchWorkspace((current) => ({
                  ...current,
                  voicePersona: {
                    ...current.voicePersona,
                    selectedPersonaId: value,
                  },
                }))
              }
              onApprovedVoiceOptionChange={(value) =>
                patchWorkspace((current) => ({
                  ...current,
                  voicePersona: {
                    ...current.voicePersona,
                    selectedVoiceOptionId: value,
                  },
                }))
              }
              onPreviewChannelChange={(value) =>
                patchWorkspace((current) => ({
                  ...current,
                  preview: {
                    ...current.preview,
                    selectedChannel: value,
                  },
                }))
              }
              onHumanizationToggleChange={(key, value) =>
                patchWorkspace((current) => ({
                  ...current,
                  humanization: {
                    ...current.humanization,
                    [key]: value,
                  },
                }))
              }
              onDeploymentNotesChange={(value) =>
                patchWorkspace((current) => ({
                  ...current,
                  deployment: {
                    ...current.deployment,
                    notes: value,
                  },
                }))
              }
              onReadinessSelectionChange={(key, value) =>
                patchWorkspace((current) => ({
                  ...current,
                  deployment: {
                    ...current.deployment,
                    readinessSelections: {
                      ...current.deployment.readinessSelections,
                      [key]: value,
                    },
                  },
                }))
              }
              onApprovalSelectionChange={(key, value) =>
                patchWorkspace((current) => ({
                  ...current,
                  deployment: {
                    ...current.deployment,
                    approvalSelections: {
                      ...current.deployment.approvalSelections,
                      [key]: value,
                    },
                  },
                }))
              }
              onRecordApproval={() =>
                patchWorkspace(
                  (current) => ({
                    ...current,
                    deployment: {
                      ...current.deployment,
                      approvalRecordedAt: new Date().toISOString(),
                    },
                  }),
                  { preserveRecordedApproval: true }
                )
              }
              onClearApproval={() =>
                patchWorkspace(
                  (current) => ({
                    ...current,
                    deployment: {
                      ...current.deployment,
                      approvalRecordedAt: null,
                    },
                  }),
                  { preserveRecordedApproval: true }
                )
              }
              onDeploymentDecisionChange={setDeploymentDecision}
              deploymentDecision={deploymentDecision}
              premiumDemo={premiumDemo}
              improveLoopModel={improveLoopModel}
              workspaceState={workspaceState}
            />

            <div
              style={{
                marginTop: "1.35rem",
                display: "flex",
                justifyContent: "space-between",
                gap: "0.8rem",
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                onClick={() => {
                  if (previousStepId) selectStep(previousStepId);
                }}
                disabled={!previousStepId}
                style={{
                  borderRadius: 999,
                  padding: "0.82rem 1.15rem",
                  border: "1px solid rgba(54, 44, 35, 0.12)",
                  background: previousStepId ? "#fff9f2" : "rgba(255, 249, 242, 0.6)",
                  color: previousStepId ? "#3e372f" : "#9d9388",
                  fontWeight: 700,
                  cursor: previousStepId ? "pointer" : "not-allowed",
                }}
              >
                {previousStep ? `Back to ${previousStep.title}` : "At first step"}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (nextStepId) selectStep(nextStepId);
                }}
                disabled={!nextStepId}
                style={{
                  borderRadius: 999,
                  padding: "0.82rem 1.15rem",
                  border: "1px solid rgba(41, 84, 103, 0.18)",
                  background: nextStepId
                    ? "linear-gradient(135deg, #173847, #285466)"
                    : "linear-gradient(135deg, rgba(23, 56, 71, 0.52), rgba(40, 84, 102, 0.52))",
                  color: "#f7f4ee",
                  fontWeight: 700,
                  cursor: nextStepId ? "pointer" : "not-allowed",
                }}
              >
                {nextStep ? `Continue to ${nextStep.title}` : "Deploy readiness reached"}
              </button>
            </div>
          </section>

          <div
            style={{
              display: "grid",
              gap: "1rem",
              position: "sticky",
              top: 18,
            }}
          >
            <StudioSidebarCard
              title={model.liveDeployment.headline}
              eyebrow="Live state"
              tone="blocked"
            >
              <p style={{ margin: 0, lineHeight: 1.6 }}>{model.liveDeployment.summary}</p>
              <ul style={{ margin: "0.85rem 0 0", paddingLeft: "1.15rem", lineHeight: 1.7 }}>
                {model.liveDeployment.blockers.map((blocker) => (
                  <li key={blocker}>{blocker}</li>
                ))}
              </ul>
            </StudioSidebarCard>

            <StudioSidebarCard
              title={model.routingTruth.title}
              eyebrow="Routing truth"
              tone="accent"
            >
              <p style={{ margin: 0, lineHeight: 1.6 }}>{model.routingTruth.summary}</p>
              <ul style={{ margin: "0.85rem 0 0", paddingLeft: "1.15rem", lineHeight: 1.7 }}>
                {model.routingTruth.facts.map((fact) => (
                  <li key={fact}>{fact}</li>
                ))}
              </ul>
            </StudioSidebarCard>

            <StudioSidebarCard title={model.previewLane.title} eyebrow="Execution lane">
              <p style={{ margin: 0, lineHeight: 1.6 }}>{model.previewLane.summary}</p>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "0.45rem",
                  marginTop: "0.85rem",
                }}
              >
                {model.previewLane.channels.map((channel) => (
                  <span
                    key={channel}
                    style={{
                      borderRadius: 999,
                      padding: "0.35rem 0.65rem",
                      background: "rgba(41, 84, 103, 0.08)",
                      color: "#295467",
                      fontSize: "0.78rem",
                      fontWeight: 700,
                    }}
                  >
                    {channel}
                  </span>
                ))}
              </div>
            </StudioSidebarCard>

            <StudioSidebarCard
              title={model.businessUserExperience.title}
              eyebrow="Founder-demo clarity"
              tone="accent"
            >
              <p style={{ margin: 0, lineHeight: 1.6 }}>{model.businessUserExperience.summary}</p>
              <ul style={{ margin: "0.85rem 0 0", paddingLeft: "1.15rem", lineHeight: 1.7 }}>
                {model.businessUserExperience.nextMove.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </StudioSidebarCard>

            <section
              aria-label="Studio summary"
              style={{
                display: "grid",
                gap: "0.75rem",
              }}
            >
              {model.controls.map((control) => (
                <div
                  key={control.label}
                  style={{
                    ...controlCardStyles(control.state),
                    borderRadius: 20,
                    padding: "0.95rem",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.74rem",
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      color: "#7b726a",
                      fontWeight: 700,
                    }}
                  >
                    {control.label}
                  </div>
                  <div style={{ marginTop: "0.35rem", fontWeight: 700, color: "#28231f" }}>
                    {control.value}
                  </div>
                  <p
                    style={{
                      margin: "0.35rem 0 0",
                      fontSize: "0.9rem",
                      lineHeight: 1.55,
                      color: "#5b534b",
                    }}
                  >
                    {control.detail}
                  </p>
                </div>
              ))}
            </section>
          </div>
        </section>
      </div>
    </main>
  );
}
